import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { SectionWithCharts } from 'pages/Benchmarking/performance/components/SectionWithCharts';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useEffect } from 'react';
import { PerformanceDashboardInfoContent } from 'pages/Benchmarking/performance/components/PerformanceDashboardInfoContent';
import { usePerformanceData } from './hooks/usePerformanceData';
import { useAppStore } from 'hooks/state/useAppStore';
import { ToggleButtonGroup } from '@mui/material';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { useIntl } from 'react-intl';

export function PerformanceDashboard() {
  const { performanceData, ordGmvVal, retQtyRateOD, cncQtyRate, benchmark, isLoading, isError, errorMessage } =
    usePerformanceData();

  const { setContent } = useInfoDrawer();
  const intl = useIntl();

  const { benchmarkingDateRange, benchmarkingPerformanceSection, setBenchmarkingPerformanceSection } = useAppStore();

  useEffect(() => {
    const performanceDashboardInfo = {
      titleKey: 'Performance Benchmarking',
      subtitleKey: 'info.content.all_dates_utc',
      content: <PerformanceDashboardInfoContent />,
    };

    setContent(performanceDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  if (isLoading && !performanceData) return <MetricsLoadingSpinner />;

  if (isError) {
    return (
      <PageContainer>
        <Tile
          titleId="benchmarking.performance"
          errorDetails={errorMessage}
          testId="benchmarking-performance-dashboard-error"
        />
      </PageContainer>
    );
  }

  if (!performanceData) return <MetricsLoadingSpinner />;

  const sectionToggle = (
    <ToggleButtonGroup
      exclusive
      value={benchmarkingPerformanceSection}
      onChange={(_event, value: 'yoy' | 'mom' | null) => {
        if (value !== null) setBenchmarkingPerformanceSection(value);
      }}
    >
      <StyledToggleButton
        size="small"
        className="whitespace-nowrap"
        value="yoy"
        sx={{ color: '#1A1A1A' }}
        data-testid="bm-performance-toggle-yoy"
      >
        {intl.formatMessage({ id: 'benchmarking.performance.toggle.yoy' })}
      </StyledToggleButton>
      <StyledToggleButton
        size="small"
        className="whitespace-nowrap"
        value="mom"
        sx={{ color: '#1A1A1A' }}
        data-testid="bm-performance-toggle-mom"
      >
        {benchmarkingDateRange.temporalType === 'WEEKLY'
          ? intl.formatMessage({ id: 'benchmarking.performance.toggle.weekly' })
          : intl.formatMessage({ id: 'benchmarking.performance.toggle.monthly' })}
      </StyledToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <PageContainer>
      {benchmarkingPerformanceSection === 'yoy' && (
        <SectionWithCharts
          titleId="benchmarking.performance.section.yearly"
          performance={performanceData.gmvYoY}
          measure={ordGmvVal}
          benchmark={benchmark}
          type="yoy"
          rightComponent={sectionToggle}
        />
      )}
      {benchmarkingPerformanceSection === 'mom' && (
        <SectionWithCharts
          titleId={`benchmarking.performance.section.pop.${benchmarkingDateRange.temporalType.toLowerCase()}`}
          performance={performanceData.gmvPoP}
          measure={ordGmvVal}
          benchmark={benchmark}
          type="mom"
          rightComponent={sectionToggle}
        />
      )}
      <SectionWithCharts
        titleId="benchmarking.performance.section.returns"
        performance={performanceData.returnRate}
        measure={retQtyRateOD}
        benchmark={benchmark}
        type="returns"
      />
      <SectionWithCharts
        titleId="benchmarking.performance.section.cancellations"
        performance={performanceData.cancellationRate}
        measure={cncQtyRate}
        benchmark={benchmark}
        type="cancellations"
      />
    </PageContainer>
  );
}
